import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, Field, LField, ItemList, Toggle } from "@/components/admin/CmsControls";

function useCms(key) {
  const [value, setValue] = useState(null);
  useEffect(() => { api.get(`/admin/content/${key}`).then((r) => setValue(r.data.value || {})).catch(() => setValue({})); }, [key]);
  const save = async () => {
    try { await api.put("/admin/content", { key, value }); toast.success("Saved & published"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const set = (path, v) => setValue((prev) => {
    const next = { ...prev };
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = { ...(cur[keys[i]] || {}) }; cur = cur[keys[i]]; }
    cur[keys[keys.length - 1]] = v;
    return next;
  });
  return { value, set, setValue, save };
}

const SaveBar = ({ onSave, tid }) => (
  <div className="flex justify-end pt-1">
    <Button onClick={onSave} className="gradient-brand text-brand-navy font-semibold" data-testid={tid}><Save className="h-4 w-4 mr-1.5" /> Save & Publish</Button>
  </div>
);

function HomeEditor() {
  const { value, set, setValue, save } = useCms("home");
  if (!value) return null;
  const v = value;
  return (
    <div className="space-y-3 max-w-3xl">
      <Collapsible title="Hero" defaultOpen>
        <LField label="Eyebrow / small label" value={v.hero?.eyebrow} onChange={(x) => set("hero.eyebrow", x)} testid="home-hero-eyebrow" placeholder="Premium Trading Ecosystem" />
        <LField label="Main headline" value={v.hero?.title} onChange={(x) => set("hero.title", x)} testid="home-hero-title" placeholder="INVIXA TRADERS" />
        <LField label="Highlighted headline" value={v.hero?.highlight} onChange={(x) => set("hero.highlight", x)} placeholder="Your Complete Trading Ecosystem" />
        <LField label="Subtitle" value={v.hero?.subtitle} onChange={(x) => set("hero.subtitle", x)} multiline />
        <div className="grid grid-cols-2 gap-3">
          <LField label="Primary CTA text" value={v.hero?.cta1_text} onChange={(x) => set("hero.cta1_text", x)} placeholder="Join INVIXA VIP" />
          <Field label="Primary CTA link" value={v.hero?.cta1_link} onChange={(x) => set("hero.cta1_link", x)} placeholder="/vip" />
          <LField label="Secondary CTA text" value={v.hero?.cta2_text} onChange={(x) => set("hero.cta2_text", x)} placeholder="Open Trading Account" />
          <Field label="Secondary CTA link" value={v.hero?.cta2_link} onChange={(x) => set("hero.cta2_link", x)} placeholder="/brokers" />
        </div>
      </Collapsible>

      <Collapsible title="Hero Media" subtitle="Image shown on the right side of the Home hero">
        <ImageUpload value={v.hero?.image_file_id} onChange={(id) => set("hero.image_file_id", id)} label="Hero Image" accept="image/jpeg,image/jpg,image/png,image/webp" />
        <p className="text-xs text-slate-500">Recommended: 1200 × 900 px or similar 4:3 ratio. Allowed: JPG, JPEG, PNG, WebP.</p>
        {!v.hero?.image_file_id && <p className="text-[11px] text-slate-500">No custom image uploaded — the default hero image is shown.</p>}
        <LField label="Alt text" value={v.hero?.image_alt} onChange={(x) => set("hero.image_alt", x)} testid="home-hero-alt" placeholder="Describe the image for accessibility & SEO" />
      </Collapsible>

      <Collapsible title="Why INVIXA / Intro">
        <LField label="Section title" value={v.why?.title} onChange={(x) => set("why.title", x)} />
        <LField label="Description" value={v.why?.description} onChange={(x) => set("why.description", x)} multiline />
      </Collapsible>

      <Collapsible title="Services / Ecosystem" subtitle="Editable cards shown in the Why section">
        <ItemList items={v.services} setItems={(items) => set("services", items)}
          newItem={{ title: { en: "", ar: "" }, description: { en: "", ar: "" }, icon: "Sparkles", active: true }}
          addLabel="Add service card"
          fields={[
            { key: "title", label: "Title", type: "ltext" },
            { key: "description", label: "Description", type: "ltextarea", rows: 2 },
            { key: "icon", type: "icon" },
          ]} />
      </Collapsible>

      <Collapsible title="Homepage Sections" subtitle="Toggle visibility & edit headings. Actual content stays in its own system.">
        {[["education", "Education"], ["results", "Results"], ["testimonials", "Testimonials"], ["faq", "FAQ"]].map(([k, label]) => (
          <div key={k} className="rounded-lg border border-white/10 p-3 space-y-2">
            <Toggle label={`Show ${label} section`} checked={v.sections?.[k]?.enabled !== false} onChange={(x) => set(`sections.${k}.enabled`, x)} testid={`home-sec-${k}`} />
            <LField label="Heading" value={v.sections?.[k]?.title} onChange={(x) => set(`sections.${k}.title`, x)} />
          </div>
        ))}
      </Collapsible>

      <Collapsible title="Final CTA">
        <LField label="Heading" value={v.final_cta?.heading} onChange={(x) => set("final_cta.heading", x)} />
        <LField label="Description" value={v.final_cta?.description} onChange={(x) => set("final_cta.description", x)} multiline />
        <div className="grid grid-cols-2 gap-3">
          <LField label="CTA text" value={v.final_cta?.cta_text} onChange={(x) => set("final_cta.cta_text", x)} />
          <Field label="CTA link" value={v.final_cta?.cta_link} onChange={(x) => set("final_cta.cta_link", x)} placeholder="/signup" />
        </div>
      </Collapsible>
      <SaveBar onSave={save} tid="save-content-home" />
    </div>
  );
}

function AboutEditor() {
  const { value, set, save } = useCms("about");
  if (!value) return null;
  const v = value;
  return (
    <div className="space-y-3 max-w-3xl">
      <Collapsible title="Intro" defaultOpen>
        <LField label="Title" value={v.intro?.title ?? v.title} onChange={(x) => set("intro.title", x)} testid="about-intro-title" />
        <LField label="Description" value={v.intro?.description ?? v.body} onChange={(x) => set("intro.description", x)} multiline rows={4} />
      </Collapsible>
      <Collapsible title="Who We Are">
        <LField label="Heading" value={v.who?.heading} onChange={(x) => set("who.heading", x)} />
        <LField label="Body" value={v.who?.body} onChange={(x) => set("who.body", x)} multiline rows={4} />
      </Collapsible>
      <Collapsible title="What We Do">
        <LField label="Heading" value={v.what?.heading} onChange={(x) => set("what.heading", x)} />
        <LField label="Body" value={v.what?.body} onChange={(x) => set("what.body", x)} multiline rows={4} />
      </Collapsible>
      <Collapsible title="Values / Principles">
        <ItemList items={v.values} setItems={(items) => set("values", items)}
          newItem={{ title: { en: "", ar: "" }, description: { en: "", ar: "" }, icon: "ShieldCheck", active: true }}
          addLabel="Add value"
          fields={[{ key: "title", label: "Title", type: "ltext" }, { key: "description", label: "Description", type: "ltextarea", rows: 2 }, { key: "icon", type: "icon" }]} />
      </Collapsible>
      <Collapsible title="Final CTA">
        <LField label="Heading" value={v.final_cta?.heading} onChange={(x) => set("final_cta.heading", x)} />
        <LField label="Description" value={v.final_cta?.description} onChange={(x) => set("final_cta.description", x)} multiline />
        <div className="grid grid-cols-2 gap-3">
          <LField label="CTA text" value={v.final_cta?.cta_text} onChange={(x) => set("final_cta.cta_text", x)} />
          <Field label="CTA link" value={v.final_cta?.cta_link} onChange={(x) => set("final_cta.cta_link", x)} />
        </div>
      </Collapsible>
      <p className="text-xs text-slate-500 px-1">Do not add company registration, addresses, licenses, or performance numbers unless verified by INVIXA.</p>
      <SaveBar onSave={save} tid="save-content-about" />
    </div>
  );
}

function VipEditor() {
  const { value, set, save } = useCms("vip");
  if (!value) return null;
  const v = value;
  return (
    <div className="space-y-3 max-w-3xl">
      <Collapsible title="Hero" defaultOpen>
        <LField label="Heading" value={v.hero?.heading} onChange={(x) => set("hero.heading", x)} testid="vip-hero-heading" />
        <LField label="Description" value={v.hero?.description ?? v.intro} onChange={(x) => set("hero.description", x)} multiline rows={3} />
      </Collapsible>
      <Collapsible title="Benefits">
        <ItemList items={v.benefits} setItems={(items) => set("benefits", items)}
          newItem={{ title: { en: "", ar: "" }, description: { en: "", ar: "" }, icon: "Crown", active: true }}
          addLabel="Add benefit"
          fields={[{ key: "title", label: "Title", type: "ltext" }, { key: "description", label: "Description", type: "ltextarea", rows: 2 }, { key: "icon", type: "icon" }]} />
      </Collapsible>
      <Collapsible title="How to Join" subtitle="Presentation steps only — real eligibility comes from VIP/Broker config.">
        <ItemList items={v.steps} setItems={(items) => set("steps", items)}
          newItem={{ step: "", title: { en: "", ar: "" }, description: { en: "", ar: "" }, active: true }}
          addLabel="Add step"
          fields={[{ key: "step", label: "Step number" }, { key: "title", label: "Title", type: "ltext" }, { key: "description", label: "Description", type: "ltextarea", rows: 2 }]} />
      </Collapsible>
      <Collapsible title="CTA Area">
        <LField label="Heading" value={v.cta?.heading} onChange={(x) => set("cta.heading", x)} />
        <LField label="Description" value={v.cta?.description} onChange={(x) => set("cta.description", x)} multiline />
        <LField label="CTA label" value={v.cta?.cta_label} onChange={(x) => set("cta.cta_label", x)} placeholder="Join INVIXA VIP" />
      </Collapsible>
      <p className="text-xs text-slate-500 px-1">VIP pricing & broker benefits are managed in the VIP and Brokers sections — not here.</p>
      <SaveBar onSave={save} tid="save-content-vip" />
    </div>
  );
}

function ContactEditor() {
  const { value, set, save } = useCms("contact");
  if (!value) return null;
  const v = value;
  return (
    <div className="space-y-3 max-w-3xl">
      <Collapsible title="Page Content" defaultOpen>
        <LField label="Page heading" value={v.heading} onChange={(x) => set("heading", x)} testid="contact-heading" />
        <LField label="Intro text" value={v.intro ?? v.note} onChange={(x) => set("intro", x)} multiline />
      </Collapsible>
      <Collapsible title="Contact Channels" defaultOpen>
        <Field label="Support email" value={v.support_email} onChange={(x) => set("support_email", x)} testid="contact-support-email" placeholder="support@invixa.com" />
        <div className="grid grid-cols-2 gap-3">
          <LField label="WhatsApp label" value={v.whatsapp_label} onChange={(x) => set("whatsapp_label", x)} placeholder="Chat on WhatsApp" />
          <Field label="WhatsApp URL / number" value={v.whatsapp_url} onChange={(x) => set("whatsapp_url", x)} placeholder="https://wa.me/..." />
          <LField label="Telegram label" value={v.telegram_label} onChange={(x) => set("telegram_label", x)} placeholder="Message on Telegram" />
          <Field label="Telegram URL" value={v.telegram_url} onChange={(x) => set("telegram_url", x)} placeholder="https://t.me/..." />
        </div>
        <LField label="Business / support note" value={v.business_note} onChange={(x) => set("business_note", x)} multiline />
      </Collapsible>
      <SaveBar onSave={save} tid="save-content-contact" />
    </div>
  );
}

export default function AdminContent() {
  useSEO("Admin · Site Content");
  return (
    <div>
      <AdminHeader title="Site Content (CMS)" subtitle="Edit public website copy & section presentation — content data stays in its own systems" />
      <Tabs defaultValue="home">
        <TabsList className="bg-brand-surface border border-white/10">
          <TabsTrigger value="home" data-testid="content-tab-home">Home</TabsTrigger>
          <TabsTrigger value="about" data-testid="content-tab-about">About</TabsTrigger>
          <TabsTrigger value="vip" data-testid="content-tab-vip">VIP</TabsTrigger>
          <TabsTrigger value="contact" data-testid="content-tab-contact">Contact</TabsTrigger>
        </TabsList>
        <TabsContent value="home" className="mt-4"><HomeEditor /></TabsContent>
        <TabsContent value="about" className="mt-4"><AboutEditor /></TabsContent>
        <TabsContent value="vip" className="mt-4"><VipEditor /></TabsContent>
        <TabsContent value="contact" className="mt-4"><ContactEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
